import { useEffect, useRef } from "react";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import s from "./MapPreview.module.scss";

type Pin = { lat: number; lng: number };

/**
 * A still map with one pin (Google Maps is loaded app-wide). It is a picture to screen readers (`role="img"` + `label`)
 * and takes no pointer, keyboard or tab focus. Without `window.google` (tests, Maps failed to load) a plain box shows the numbers.
 */
export const MapPreview = ({ lat, lng, label }: { lat: number; lng: number; label: string }) => {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const marker = useRef<any>(null);
  const pin = useRef<Pin>({ lat, lng });
  const maps: any = typeof window !== "undefined" ? (window as any).google?.maps : undefined;
  const live = !!maps?.Map;

  // One map per mount (StrictMode mounts twice: the cleanup drops the first one).
  useEffect(() => {
    if (!live || !box.current) return undefined;
    // Nothing inside may take focus or clicks: it is a preview, the numbers are edited in the boxes.
    box.current.setAttribute("inert", "");
    const mapId = process.env.REACT_APP_GOOGLE_MAP_ID;
    const at: Pin = { lat: pin.current.lat, lng: pin.current.lng };
    map.current = new maps.Map(box.current, {
      center: at, zoom: 17, disableDefaultUI: true, gestureHandling: "none", keyboardShortcuts: false, clickableIcons: false,
      ...(mapId ? { mapId } : {}),
    });
    const Advanced = maps.marker?.AdvancedMarkerElement;
    marker.current = mapId && Advanced ? new Advanced({ map: map.current, position: at }) : new maps.Marker({ map: map.current, position: at });
    return () => {
      if (typeof marker.current?.setMap === "function") marker.current.setMap(null);
      else if (marker.current) marker.current.map = null;
      marker.current = null;
      map.current = null;
    };
  }, [live, maps]);

  useEffect(() => {
    pin.current = { lat, lng };
    if (!map.current || !marker.current) return;
    const at: Pin = { lat, lng };
    map.current.setCenter(at);
    if (typeof marker.current.setPosition === "function") marker.current.setPosition(at);
    else marker.current.position = at;
  }, [lat, lng]);

  return (
    <div role="img" aria-label={label} className={s.preview}>
      {live ? <div ref={box} className={s.map} /> : (
        <div className={s.plain} aria-hidden>
          <PlaceRoundedIcon className={s.pin} />
          <bdi dir="ltr">{lat}, {lng}</bdi>
        </div>
      )}
    </div>
  );
};
